import React, { useState, useMemo } from "react";
import { Box, Button, TextField, InputAdornment, IconButton, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import AlarmActionsModal from "./AlarmActionsModal";
import { useQuery } from "@tanstack/react-query";
import { getSiteAlarmActionsQuery } from "../../lib/Queries";

const SiteAlarmActions = ({ panelSx, siteUuid, assets, upkeepLocationId, onNavigateToIntegrations }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("new");
  const [selectedAction, setSelectedAction] = useState(null);
  const [search, setSearch] = useState("");
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const { data: actions = [], isLoading } = useQuery({
    ...getSiteAlarmActionsQuery(siteUuid),
    enabled: Boolean(siteUuid),
  });

  const rows = useMemo(() => {
    const base = (actions || []).map((action, index) => ({
      id: action?.action_uuid,
      name: action?.action_name || "",
      raw: action,
    }));
    const term = search.trim().toLowerCase();
    if (!term) return base;
    return base.filter((r) => r.name.toLowerCase().includes(term));
  }, [actions, search]);

  const handleNewAction = () => {
    setSelectedAction(null);
    setModalMode("new");
    setRowSelectionModel([]);
    setIsModalOpen(true);
  };

  const handleRowClick = (params) => {
    setSelectedAction(params?.row?.raw || null);
    setModalMode("edit");
    setRowSelectionModel([]);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRowSelectionModel([]);
  };

  const columns = [
    { field: "name", headerName: "Alarm Actions", flex: 2 },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 64,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(params);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}>
        <Button
          variant="contained"
          size="small"
          color="primary"
          sx={{ minWidth: 180 }}
          onClick={handleNewAction}
        >
          New Action
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            placeholder="Search actions"
            variant="outlined"
            size="small"
            sx={{ width: 320 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <div style={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            hideFooter
            loading={isLoading}
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
          />
        </div>
      </Box>
      <AlarmActionsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        action={selectedAction}
        assets={assets}
        siteUuid={siteUuid}
        upkeepLocationId={upkeepLocationId}
        onNavigateToIntegrations={onNavigateToIntegrations}
      />
    </>
  );
};

SiteAlarmActions.propTypes = {
  panelSx: PropTypes.object,
  siteUuid: PropTypes.string,
  assets: PropTypes.array,
  upkeepLocationId: PropTypes.string,
  onNavigateToIntegrations: PropTypes.func,
};

export default SiteAlarmActions;
